import { useMemo, Fragment } from 'react'
import clsx from 'clsx'
import { Text } from '../../../components/modules/ui'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// Formatea "YYYY-MM" a nombre de mes
function formatMonth(m) {
  const monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre'
  ]
  if (typeof m !== 'string') return m
  const parts = m.split('-')
  if (parts.length === 2) {
    const idx = parseInt(parts[1], 10) - 1
    return monthNames[idx] || m
  }
  return m
}

// Calcula totales por sección (usa solo isGroup si hay, si no, usa todo)
function sumSection(children, uniqueMonths) {
  const base = children.some(c => c.isGroup) ? children.filter(c => c.isGroup) : children
  const monthlySum = {}
  uniqueMonths.forEach(m => {
    monthlySum[m] = base.reduce((acc, it) => acc + (it.monthlyData[m] ?? 0), 0)
  })
  const acumuladoSum = base.reduce((acc, it) => acc + (it.finalAcumulado ?? 0), 0)
  return { monthlySum, acumuladoSum }
}

const formatReportAmount = value => {
  const numericValue = Number(value ?? 0)
  const formattedValue = new Intl.NumberFormat('es-CL', {
    maximumFractionDigits: 0
  }).format(Math.abs(numericValue))

  return numericValue < 0 ? `(${formattedValue})` : formattedValue
}

const getAmountToneClass = value => {
  const amount = Number(value ?? 0)
  if (amount === 0) return 'text-slate-400 font-normal'
  if (amount < 0) return 'text-slate-700'
  return 'text-slate-700'
}

export default function TableExtendPivotSoloSaldo({ rows, headers }) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const safeRows = Array.isArray(rows) ? rows : []
  const { pivotedData, uniqueMonths } = useMemo(() => pivotData(safeRows), [safeRows])

  if (safeRows.length === 0) {
    return <Text className='grid place-items-center py-12 text-muted font-sans'>No hay datos</Text>
  }

  // Separa la fila TOTAL
  const totalRow = pivotedData.find(r => r.codeCuenta === 'TOTAL')
  const dataRows = pivotedData.filter(r => r.codeCuenta !== 'TOTAL')

  // Validamos que lleguen como [{ codigoFinal, nameFinal }, ...]
  const validHeaders =
    Array.isArray(headers) &&
    headers.every(h => typeof h.codigoFinal === 'string' && typeof h.nameFinal === 'string')

  if (isMobile) {
    const renderMonthsGrid = (values, formatter = formatReportAmount) => (
      <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs font-sans text-text'>
        {uniqueMonths.map(m => (
          <div
            key={m}
            className='rounded-lg border border-slate-200 bg-white px-2 py-2'
          >
            <div className='text-[11px] text-slate-500'>
              {formatMonth(m)}
            </div>
            <div
              className={clsx(
                'text-sm font-semibold tabular-nums',
                getAmountToneClass(values?.[m])
              )}
            >
              {formatter(values?.[m])}
            </div>
          </div>
        ))}
      </div>
    )

    const renderAcumulado = (value, formatter = formatReportAmount, isResult = false) => (
      <div className='mt-2 rounded-lg border border-slate-200 bg-white px-2 py-2'>
        <div className='text-[11px] font-semibold text-slate-700'>Acumulado</div>
        <div
          className={clsx(
            'text-sm font-semibold tabular-nums',
            getAmountToneClass(value, isResult)
          )}
        >
          {formatter(value)}
        </div>
      </div>
    )

    return (
      <div className='space-y-3 font-sans text-text'>
        {validHeaders
          ? headers.map(header => {
              const [x, y] = header.codigoFinal.split('-')
              const prefix = `${x}-${y}-`
              const children = dataRows.filter(r => r.codeCuenta.startsWith(prefix))
              const { monthlySum, acumuladoSum } = sumSection(children, uniqueMonths)

              return (
                <Fragment key={header.codigoFinal}>
                  <div className='rounded-xl border border-slate-200 bg-slate-50 p-3'>
                    <div className='text-xs font-bold text-slate-800'>{header.nameFinal}</div>
                    {renderMonthsGrid(monthlySum)}
                    {renderAcumulado(acumuladoSum)}
                  </div>

                  {children.map(item => (
                    <div
                      key={item.codeCuenta}
                      className={clsx(
                        'rounded-xl border border-slate-200 bg-white p-3',
                        item.isGroup && 'bg-slate-50'
                      )}
                    >
                      <div
                        className={clsx(
                          'text-xs',
                          item.isGroup
                            ? 'font-bold text-slate-800'
                            : 'pl-5 font-normal text-slate-600'
                        )}
                      >
                        {item.codeCuenta} {item.nameCuenta}
                      </div>
                      {renderMonthsGrid(item.monthlyData)}
                      {renderAcumulado(item.finalAcumulado)}
                    </div>
                  ))}
                </Fragment>
              )
            })
          : dataRows.map(item => (
              <div
                key={item.codeCuenta}
                className={clsx(
                  'rounded-2xl border border-border bg-surface p-3',
                  item.isGroup && 'bg-surface/70'
                )}
              >
                <div className='text-xs font-semibold text-text'>
                  {item.codeCuenta} {item.nameCuenta}
                </div>
                {renderMonthsGrid(item.monthlyData)}
                {renderAcumulado(item.finalAcumulado)}
              </div>
            ))}

        {totalRow && (
          <div className='rounded-xl border border-slate-300 bg-slate-50 p-3'>
            <div className='text-xs font-bold text-slate-900'>RESULTADO</div>
            {renderMonthsGrid(totalRow.monthlyData, formatReportAmount)}
            {renderAcumulado(totalRow.finalAcumulado, formatReportAmount, true)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='relative max-h-[60vh] w-full overflow-y-auto overflow-x-auto rounded-lg border border-slate-200 bg-white font-sans text-[10px] sm:text-[11px] text-slate-700 leading-5'>
      <table
        className='table-auto min-w-full border-separate border-spacing-0 text-[10px] sm:text-[11px]'
        style={{ tableLayout: 'auto' }}
      >
        <thead>
          <tr>
            <th className='sticky left-0 top-0 z-30 min-w-[240px] max-w-[280px] whitespace-normal break-words border-b border-slate-200 bg-white px-4 py-3 text-left text-[9px] font-bold uppercase tracking-wide text-slate-500 sm:text-[10px]'>
              CUENTA
            </th>
            {uniqueMonths.map(m => {
              return (
                <th
                  key={m}
                  className='sticky top-0 z-20 min-w-[104px] whitespace-nowrap border-b border-slate-200 bg-white px-3 py-3 text-right text-[9px] font-bold uppercase tracking-wide text-slate-500 sm:text-[10px]'
                >
                  {formatMonth(m)}
                </th>
              )
            })}
            <th className='sticky top-0 z-20 min-w-[120px] whitespace-nowrap border-b border-l border-slate-200 bg-white px-3 py-3 text-right text-[9px] font-semibold uppercase tracking-wide text-slate-700 sm:text-[10px]'>
              ACUMULADO
            </th>
          </tr>
        </thead>
        <tbody>
          {validHeaders
            ? headers.map(header => {
                // Filtra hijos cuyo codeCuenta empieza con "X-Y-"
                const [x, y] = header.codigoFinal.split('-')
                const prefix = `${x}-${y}-`
                const children = dataRows.filter(r => r.codeCuenta.startsWith(prefix))

                // NUEVO: totales de la sección
                const { monthlySum, acumuladoSum } = sumSection(children, uniqueMonths)

                return (
                  <Fragment key={header.codigoFinal}>
                    {/* Fila de sección con SUMAS */}
                    <tr className='group bg-slate-50 hover:bg-slate-100/70'>
                      <td className='sticky left-0 z-10 max-w-[280px] whitespace-normal break-words border-b border-t border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-800 group-hover:bg-slate-100/70 sm:text-[11px]'>
                        {header.nameFinal}
                      </td>
                      {uniqueMonths.map(m => {
                        return (
                          <td
                            key={m}
                            className={clsx(
                              'whitespace-nowrap border-b border-t border-slate-200 px-3 py-3 text-right text-[10px] font-bold tabular-nums sm:text-[11px]',
                              getAmountToneClass(monthlySum[m])
                            )}
                          >
                            {formatReportAmount(monthlySum[m])}
                          </td>
                        )
                      })}
                      <td
                        className={clsx(
                          'whitespace-nowrap border-b border-l border-t border-slate-200 px-3 py-3 text-right text-[10px] font-semibold tabular-nums sm:text-[11px]',
                          getAmountToneClass(acumuladoSum)
                        )}
                      >
                        {formatReportAmount(acumuladoSum)}
                      </td>
                    </tr>

                    {/* Filas hijas */}
                    {children.map(item => {
                      const rowClasses = item.isGroup
                        ? 'bg-white font-semibold text-slate-800'
                        : 'bg-white text-slate-600'
                      return (
                        <tr
                          key={item.codeCuenta}
                          className={clsx('group hover:bg-slate-50', rowClasses)}
                        >
                          <td
                            className={clsx(
                              'sticky left-0 z-10 max-w-[280px] whitespace-normal break-words border-b border-slate-100 bg-white py-2.5 pr-4 text-left text-[10px] group-hover:bg-slate-50 sm:text-[11px]',
                              item.isGroup
                                ? 'pl-8 font-bold text-slate-800'
                                : 'pl-14 font-normal text-slate-600'
                            )}
                          >
                            <span>
                              {item.codeCuenta} {item.nameCuenta}
                            </span>
                          </td>
                          {uniqueMonths.map(m => {
                            return (
                              <td
                                key={m}
                                className={clsx(
                                  'whitespace-nowrap border-b border-slate-100 px-3 py-2.5 text-right text-[10px] tabular-nums sm:text-[11px]',
                                  item.isGroup && 'font-bold',
                                  getAmountToneClass(item.monthlyData[m])
                                )}
                              >
                                {formatReportAmount(item.monthlyData[m])}
                              </td>
                            )
                          })}
                          <td
                            className={clsx(
                              'whitespace-nowrap border-b border-l border-slate-200 px-3 py-2.5 text-right text-[10px] font-semibold tabular-nums sm:text-[11px]',
                              getAmountToneClass(item.finalAcumulado)
                            )}
                          >
                            {formatReportAmount(item.finalAcumulado)}
                          </td>
                        </tr>
                      )
                    })}
                  </Fragment>
                )
              })
            : // Fallback: lista plana sin agrupar
              dataRows.map(item => {
                const rowClasses = item.isGroup
                  ? 'bg-white font-semibold text-slate-800'
                  : 'bg-white text-slate-600'
                return (
                  <tr key={item.codeCuenta} className={clsx('group hover:bg-slate-50', rowClasses)}>
                    <td
                      className={clsx(
                        'sticky left-0 z-10 max-w-[280px] whitespace-normal break-words border-b border-slate-100 bg-white py-2.5 pr-4 text-left text-[10px] group-hover:bg-slate-50 sm:text-[11px]',
                        item.isGroup
                          ? 'pl-8 font-bold text-slate-800'
                          : 'pl-14 font-normal text-slate-600'
                      )}
                    >
                      {item.codeCuenta} {item.nameCuenta}
                    </td>
                    {uniqueMonths.map(m => {
                      return (
                        <td
                          key={m}
                          className={clsx(
                            'whitespace-nowrap border-b border-slate-100 px-3 py-2.5 text-right text-[10px] tabular-nums sm:text-[11px]',
                            item.isGroup && 'font-bold',
                            getAmountToneClass(item.monthlyData[m])
                          )}
                        >
                          {formatReportAmount(item.monthlyData[m])}
                        </td>
                      )
                    })}
                    <td
                      className={clsx(
                        'whitespace-nowrap border-b border-l border-slate-200 px-3 py-2.5 text-right text-[10px] font-semibold tabular-nums sm:text-[11px]',
                        getAmountToneClass(item.finalAcumulado)
                      )}
                    >
                      {formatReportAmount(item.finalAcumulado)}
                    </td>
                  </tr>
                )
              })}

          {/* Fila TOTAL */}
          {totalRow && (
            <tr className='group bg-slate-50 hover:bg-slate-100/70'>
              <td className='sticky left-0 z-10 max-w-[280px] whitespace-normal break-words border-t border-slate-300 bg-slate-50 px-4 py-3 text-left text-[10px] font-bold text-slate-900 group-hover:bg-slate-100/70 sm:text-[11px]'>
                RESULTADO
              </td>
              {uniqueMonths.map(m => {
                return (
                  <td
                    key={m}
                    className={clsx(
                      'whitespace-nowrap border-t border-slate-300 px-3 py-3 text-right text-[10px] font-bold tabular-nums sm:text-[11px]',
                      getAmountToneClass(totalRow.monthlyData[m], true)
                    )}
                  >
                    {formatReportAmount(totalRow.monthlyData[m])}
                  </td>
                )
              })}
              <td
                className={clsx(
                  'whitespace-nowrap border-l border-t border-slate-200 px-3 py-3 text-right text-[10px] font-semibold tabular-nums sm:text-[11px]',
                  getAmountToneClass(totalRow.finalAcumulado, true)
                )}
              >
                {formatReportAmount(totalRow.finalAcumulado)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function pivotData(rows) {
  // 1) Meses únicos, descarta falsy
  const uniqueMonths = Array.from(new Set(rows.map(r => r.mes).filter(Boolean))).sort((a, b) => {
    const [ya, ma] = a.split('-').map(Number)
    const [yb, mb] = b.split('-').map(Number)
    return ya - yb || ma - mb
  })

  // 2) Agrupar por cuenta
  const dataByCuenta = new Map()
  rows.forEach(item => {
    const key = item.codeCuenta
    if (!dataByCuenta.has(key)) {
      dataByCuenta.set(key, {
        codeCuenta: key,
        nameCuenta: item.nameCuenta,
        monthlyData: {},
        finalAcumulado: 0,
        isGroup: !!item.isGroup
      })
    }
    const obj = dataByCuenta.get(key)
    obj.isGroup = obj.isGroup || !!item.isGroup
    const neto = Number(item.calculoNeto ?? 0)
    obj.monthlyData[item.mes] = (obj.monthlyData[item.mes] ?? 0) + neto
    obj.finalAcumulado += neto
  })

  // 3) A array y ordenar
  const pivoted = Array.from(dataByCuenta.values()).sort((a, b) =>
    a.codeCuenta.localeCompare(b.codeCuenta)
  )

  // 4) TOTAL (suma solo grupos para evitar doble conteo)
  const totalRow = {
    codeCuenta: 'TOTAL',
    nameCuenta: '',
    monthlyData: {},
    finalAcumulado: 0,
    isGroup: true
  }
  uniqueMonths.forEach(m => {
    totalRow.monthlyData[m] = 0
  })
  pivoted.forEach(item => {
    if (!item.isGroup) return
    uniqueMonths.forEach(m => {
      totalRow.monthlyData[m] += item.monthlyData[m] ?? 0
    })
    totalRow.finalAcumulado += item.finalAcumulado
  })
  pivoted.push(totalRow)

  return { pivotedData: pivoted, uniqueMonths }
}
